// #2086: pure decision logic for two of the onboarding-tour-refresh's
// scope items, split out of Dashboard.tsx so they're unit-testable without
// mounting the whole dashboard (ArcGIS map, auth context, watchlist hook,
// property fetch, react-router — Dashboard.tsx has never had its own test
// file for exactly that reason). Dashboard renders the derived shape;
// these functions own the actual decision.

/**
 * Item 3 ("land somewhere with data"): the county-picker's suggested
 * county used to fall back to whatever county the user happened to be
 * browsing (localStorage / the default), which can itself have zero live
 * inventory — the picker would then suggest, and the user would pick, a
 * county that lands them on an empty screen. This resolves the same
 * suggestion but prefers `defaultCountyId` (the flagship county with the
 * deepest real coverage) over a currently-loaded, confirmed-empty county.
 *
 * A real watchlist signal (`bestWatchlistCounty`) wins over the browsing
 * fallbacks — it's a deliberate save, a stronger signal than merely having a
 * tab open.
 *
 * `requestedCounty` beats everything: it is the county a visitor arrived for
 * from a county landing page ("Browse these leads" → /app?county=<id>). That
 * link used to go to a bare /app, so a Wake visitor was suggested
 * Mecklenburg — the default — at the exact moment they were choosing.
 */
export function resolveSuggestedCounty(opts: {
  requestedCounty?: string | null;
  bestWatchlistCounty?: string;
  countyId: string;
  defaultCountyId: string;
  /** true once the current county's property fetch has settled (not still
   *  loading, didn't error) — a mid-flight or failed fetch reports 0 too,
   *  and that's not evidence the county is actually empty. */
  currentCountySettled: boolean;
  currentCountyTotal: number;
}): string {
  if (opts.requestedCounty) return opts.requestedCounty;
  if (opts.bestWatchlistCounty) return opts.bestWatchlistCounty;
  if (
    opts.currentCountySettled &&
    opts.currentCountyTotal === 0 &&
    opts.countyId !== opts.defaultCountyId
  ) {
    return opts.defaultCountyId;
  }
  return opts.countyId;
}

export interface EmptyCountyNotice {
  /** Whether a "browse the default county instead" button/action should
   *  render alongside the message. */
  canSwitchToDefault: boolean;
  /** The second sentence of the notice — varies by whether switching is
   *  actually possible for this user's plan/chosen-counties state. */
  tail: string;
}

/**
 * Item 3 ("land somewhere with data"): a chosen county that genuinely has
 * zero live properties used to render nothing — the existing "unlock"
 * banner and "thin county" banner both require totalPropertyCount > 0, so
 * a bare, unexplained empty list pane was the result. This computes the
 * notice's derived state; Dashboard.tsx supplies the JSX (so the county
 * name can render as a real bolded element rather than interpolated text).
 *
 * The switch-to-default button is only offered when it would actually
 * work: unrestricted plans, a free/scout user who hasn't picked a county
 * yet (chosenCounties empty — the county switcher allows anything then),
 * or a free/scout user whose picked set already includes the default
 * county. A free/scout user locked to a different, empty county can't
 * switch away without waiting out the 180-day cadence gate or upgrading,
 * so the message falls back to that instead of offering a dead button.
 */
export function getEmptyCountyNotice(opts: {
  countyId: string;
  defaultCountyId: string;
  isUnrestrictedPlan: boolean;
  chosenCounties: string[];
}): EmptyCountyNotice {
  const canSwitchToDefault =
    opts.countyId !== opts.defaultCountyId &&
    (opts.isUnrestrictedPlan ||
      opts.chosenCounties.length === 0 ||
      opts.chosenCounties.includes(opts.defaultCountyId));

  const tail = canSwitchToDefault
    ? "Mecklenburg County has the deepest coverage today — take a look there instead."
    : opts.isUnrestrictedPlan
    ? "Check back soon, or try a different county."
    : "Upgrade to Pro for full access to every NC county at once.";

  return { canSwitchToDefault, tail };
}

/**
 * The county a restricted (free/scout) viewer is allowed to LIST. The API 403s
 * `county_not_in_plan` on GET /properties for a county outside the viewer's
 * chosen set, so a brand-new user who picks county X in the picker while the
 * dashboard is still pointed at the default/stored county Y would otherwise hit
 * a raw "Request failed with status code 403". Returns `countyId` unchanged when
 * it is already viewable (unrestricted plan, nothing chosen yet, or chosen);
 * otherwise the first chosen county.
 */
export function resolveViewableCounty(opts: {
  countyId: string;
  isUnrestrictedPlan: boolean;
  chosenCounties: string[];
}): string {
  if (
    opts.isUnrestrictedPlan ||
    opts.chosenCounties.length === 0 ||
    opts.chosenCounties.includes(opts.countyId)
  ) {
    return opts.countyId;
  }
  return opts.chosenCounties[0];
}
